<template>
    <base-panel :panel="panel">
        <template slot="content">
            <!-- Correct & Missed Item List -->
            <div
                v-for="(discoveredItem, index) in discoveredItemsCorrectOrMissed"
                :key="'item' + index"
                class="alert border-0 mb-2 discovered-item"
                :class="{
                    'alert-success': discoveredItem.correct,
                    'alert-gray bg-light text-secondary': discoveredItem.wasMissed,
                }"
            >
                <div>
                    <span
                        v-if="discoveredItem.correct === true"
                        class="icon icon-glyphicons-basic-circle-empty-check align-middle mr-1"
                    ></span>
                    <span
                        v-else-if="discoveredItem.correct === false"
                        class="icon icon-glyphicons-basic-circle-empty-remove align-middle mr-1"
                    ></span>
                    <span
                        v-else-if="discoveredItem.wasMissed === true"
                        class="icon text-danger icon-glyphicons-basic-circle-empty-alert align-middle mr-1"
                    ></span>
                    <span class="align-middle" style="padding-left: 1px">{{ discoveredItem.label }}</span>
                </div>
                <div v-if="discoveredItem.feedbackMessage" class="text-secondary small">
                    {{ discoveredItem.feedbackMessage }}
                </div>
            </div>
            <!-- Undiscovered Item Placeholder List -->
            <div
                v-for="index in undiscoveredItemArr"
                :key="'placeholder' + index"
                class="alert border-0 alert-secondary py-2 px-3 mb-2"
            ></div>
            <!-- Non-Correct Items List  -->
            <div v-if="panel.include_non_correct_items">
                <div
                    v-for="(discoveredItem, index) in discoveredItemsNotCorrect"
                    :key="'item' + index"
                    class="alert border-0 mb-2 discovered-item"
                    :class="{
                        'alert-info': [null, undefined].includes(discoveredItem.correct),
                        'alert-danger': discoveredItem.correct === false,
                        'alert-gray bg-light text-secondary': discoveredItem.was_missed,
                    }"
                >
                    <div>
                        <span
                            v-if="discoveredItem.correct === true"
                            class="icon icon-glyphicons-basic-circle-empty-check align-middle mr-1"
                        ></span>
                        <span
                            v-else-if="discoveredItem.correct === false"
                            class="icon icon-glyphicons-basic-circle-empty-remove align-middle mr-1"
                        ></span>
                        <span
                            v-else-if="discoveredItem.wasMissed === true"
                            class="icon text-danger icon-glyphicons-basic-circle-empty-alert align-middle mr-1"
                        ></span>
                        <span class="align-middle" style="padding-left: 1px">{{ discoveredItem.label }}</span>
                    </div>
                    <div v-if="discoveredItem.feedbackMessage">
                        <span class="text-secondary small">
                            {{ discoveredItem.feedbackMessage }}
                        </span>
                    </div>
                </div>
            </div>
        </template>
    </base-panel>
</template>

<script>
import http from "@/axios.js"
import BasePanel from "@/components/panels/BasePanel"

export default {
    name: "DiscoveryPanel",
    components: {
        BasePanel,
    },
    props: {
        panel: {
            type: Object,
            required: true,
        },
        correctResponsesCurrent: {
            type: Number,
            required: false,
            default() {
                return 0
            },
        },
        correctResponsesVisible: {
            type: Number,
            required: false,
            default() {
                return 0
            }
        }
    },
    data() {
        return {
            choiceResponses: [],
            discoveredIntents: [],
            totalPossibleCorrectResponseCount: 0,
        }
    },
    computed: {
        sessionId() {
            return this.$store.getters.session.id
        },
        currentNode() {
            return this.$store.getters.sessionNode
        },
        // An array used to render placeholder gray-bars representing undiscovered correct items.
        undiscoveredItemArr() {
            let totalUndiscoveredCount = this.totalPossibleCorrectResponseCount - this.discoveredItemsCorrectOrMissed.length
            let indexArr = []
            for (let i = 0; i < totalUndiscoveredCount; i++) {
                indexArr.push(i)
            }
            return indexArr
        },
        // An array of all currently discovered items.
        discoveredItems() {
            // Place both choice responses and intents discovered into a uniform format.
            let discoveredItems = []
            // Format choice responses.
            this.choiceResponses.forEach((choiceResponse) => {
                discoveredItems.push({
                    panelId: choiceResponse.panelId,
                    label: choiceResponse.choice.text,
                    feedbackMessage: "",
                    correct: choiceResponse.choice.correct,
                    // Choice-based responses don't currently use the 'was missed' flag, so
                    // set it to false for all choices.
                    wasMissed: false,
                })
            })
            // Format discovered chat intents.
            this.discoveredIntents.forEach((discoveredIntent) => {
                let item = {
                    panelId: discoveredIntent.panelId,
                    label: discoveredIntent.message,
                    correct: discoveredIntent.correct,
                    // Choice-based responses don't currently use the 'was missed' flag, so
                    // set it to false for all choices.
                    wasMissed: discoveredIntent.was_missed,
                }
                // Only add the feedback message for legacy chat panels. For newer ones, this
                // feedback is displayed within the chat panel itself.
                if (this.panel.panel_type == "chat") {
                    item.feedbackMessage = discoveredIntent.chat_agent_intent.feedback_message
                }
                discoveredItems.push(item)
            })
            return discoveredItems
        },
        // An array of discovered items that are flagged as correct or missed.
        discoveredItemsCorrectOrMissed() {
            return this.discoveredItems.filter((d) => d.correct || d.wasMissed)
        },
        // An array of discovered items that are incorrect or neutral.
        discoveredItemsNotCorrect() {
            return this.discoveredItems.filter((d) => !d.correct && !d.wasMissed)
        },
    },
    methods: {
        // Fetch discovered items (choice responses and discovered intents) for the current node.
        refreshData() {
            if (!this.panel.linked_response_panel) {
                return
            }
            let vm = this
            http.get(`/api/students/sessions/${this.sessionId}/panel/${this.panel.linked_response_panel}/responses/`).then(function (
                response
            ) {
                vm.totalPossibleCorrectResponseCount = response.data.total_correct_visible
                let panelResponses = response.data.responses
                let choiceResponses = []
                let discoveredIntents = []
                // Loop over responses and collect ones that are valid for 'discovery'. Responses are received
                // as a dictionary keyed by the panelId they're attached to.
                Object.keys(panelResponses).forEach(function (panelId) {
                    panelResponses[panelId].choice_responses.forEach((choiceResponse) => {
                        choiceResponses.push(choiceResponse)
                    })
                    panelResponses[panelId].intents_discovered.forEach((intentDiscovered) => {
                        discoveredIntents.push(intentDiscovered)
                    })
                })
                vm.choiceResponses = choiceResponses
                vm.discoveredIntents = discoveredIntents
            })
        },
    },
    mounted() {
        this.refreshData()
    },
}
</script>

<style lang="scss" scoped>
.discovered-item {
    padding-top: 4px;
    padding-bottom: 4px;
    padding-left: 8px;
    padding-right: 8px;
}
</style>